// The statements that took a database the most time (System > database > Queries): NextAdmin `tables/basic-tables`
// in a card. Collected every 15 minutes from pg_stat_statements (normalized text: constants show as $1, $2…).
import { useQuery } from '@tanstack/react-query';
import { Database } from 'lucide-react';
import {
  Badge,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  QueryBoundary,
} from '../../../components/ui/index.js';
import { listSystemDatabaseStatementsOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import type { SystemDatabase, SystemStatement } from '../../../generated/api/types.gen.js';
import { formatBytes, formatCount, formatSeconds, type MetricRange } from './metric-format.js';

const STATEMENT_COLUMNS: DataTableColumn<SystemStatement>[] = [
  {
    key: 'query',
    header: 'Statement',
    render: (s) => (
      <code className="block max-w-xl truncate text-xs" title={s.query}>
        {s.query}
      </code>
    ),
  },
  {
    key: 'total',
    header: 'Time spent',
    align: 'right',
    sortable: true,
    sortValue: (s) => s.totalMs,
    render: (s) => formatSeconds(s.totalMs / 1000),
  },
  {
    key: 'calls',
    header: 'Calls',
    align: 'right',
    sortable: true,
    sortValue: (s) => s.calls,
    render: (s) => formatCount(s.calls),
  },
  {
    key: 'mean',
    header: 'Per call',
    align: 'right',
    sortable: true,
    sortValue: (s) => s.meanMs,
    render: (s) => formatSeconds(s.meanMs / 1000),
  },
  { key: 'rows', header: 'Rows', align: 'right', render: (s) => formatCount(s.rows) },
  { key: 'read', header: 'Read from disk', align: 'right', render: (s) => formatBytes(s.blocksRead * 8192) },
  // Temporary files: a sort or hash that did not fit in work_mem spilled to disk.
  {
    key: 'temp',
    header: 'Temp written',
    align: 'right',
    render: (s) => (s.tempBlocksWritten > 0 ? formatBytes(s.tempBlocksWritten * 8192) : '—'),
  },
];

/** pg_stat_statements' own table: a full one pushes the least-used statements out, and their history goes with them. */
function StatementTableLine({ table }: { table: NonNullable<SystemDatabase['statementTable']> }) {
  return (
    <p className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted">
      <span>
        {`Statement table: ${formatCount(table.entries)} of ${formatCount(table.max)} entries · ${formatBytes(table.textBytes)} of text · ${formatCount(table.evictions)} eviction(s) since its last reset`}
      </span>
      {table.evictions > 0 && (
        <Badge variant="warning" dot>
          Full: statistics are being pushed out
        </Badge>
      )}
    </p>
  );
}

export function StatementsCard({
  databaseId,
  range,
  collected,
  table,
}: {
  databaseId: SystemDatabase['id'];
  range: MetricRange;
  collected: boolean;
  table: SystemDatabase['statementTable'];
}) {
  const query = useQuery({
    ...listSystemDatabaseStatementsOptions({ path: { id: databaseId }, query: { range } }),
    enabled: collected,
  });
  return (
    <Card title="Statements that took the most time" className="xl:col-span-12">
      {table && <StatementTableLine table={table} />}
      {collected ? (
        <QueryBoundary query={query} label="Statements">
          {(statements) => (
            <DataTable
              columns={STATEMENT_COLUMNS}
              rows={statements}
              rowKey={(s) => s.queryId}
              empty={
                <p className="py-6 text-center text-sm text-muted">
                  No statements in this range yet (they are collected every 15 minutes).
                </p>
              }
            />
          )}
        </QueryBoundary>
      ) : (
        <EmptyState
          icon={Database}
          title="Query statistics are not on yet"
          description="This database does not load pg_stat_statements. Turning it on needs a restart of the database: see deploy/monitoring/RUNBOOK.md."
        />
      )}
    </Card>
  );
}
